import type {
  OrgLlmConnectionRecord,
  OrgLlmConnectionStatus,
} from "@/shared/domain/orgs";
import { useVerifyOrgLlmConnection } from "@/shared/domain/orgs/hooks";
import {
  ORG_LLM_CONNECTION_KIND_LABELS,
  ORG_LLM_CONNECTION_STATUS_LABELS,
} from "@/shared/domain/orgs/orgModelConfig";
import {
  Badge,
  type BadgeVariant,
  RobotoButton,
} from "@/shared/foundation/primitives";

import { verifiedAgo } from "../verification";
import { VerifyOutcome } from "../VerifyOutcome";

import styles from "./OrgLlmConnections.module.css";

const STATUS_BADGE: Record<OrgLlmConnectionStatus, BadgeVariant> = {
  active: "success",
  failing: "error",
  disabled: "warning",
};

/** Where a connection sends its calls; an Anthropic API connection without a gateway goes to Anthropic. */
function endpointLabel(connection: OrgLlmConnectionRecord): string {
  const { settings } = connection;
  if (settings.base_url !== null && settings.base_url !== undefined) {
    return settings.base_url;
  }
  return "https://api.anthropic.com (default)";
}

interface ConnectionListItemProps {
  orgId: string;
  connection: OrgLlmConnectionRecord;
  /** Admins of a premium org may verify it: call a model on it and record the outcome. */
  canVerify: boolean;
  /** Omitted when the caller may not edit it. */
  onEdit?: () => void;
  /** Omitted when the caller may not delete it. */
  onDelete?: () => void;
}

/** One connection: its kind, endpoint, key secret, status and verification. */
export function ConnectionListItem({
  orgId,
  connection,
  canVerify,
  onEdit,
  onDelete,
}: ConnectionListItemProps) {
  const verifyMutation = useVerifyOrgLlmConnection();
  return (
    <li className={styles.item} aria-label={connection.label}>
      <div className={styles.itemHeader}>
        <span className={styles.itemLabel}>{connection.label}</span>
        <Badge variant={STATUS_BADGE[connection.status]}>
          {ORG_LLM_CONNECTION_STATUS_LABELS[connection.status]}
        </Badge>
        {connection.last_verified_at === null ? (
          <Badge variant="secondary">Not verified yet</Badge>
        ) : (
          <Badge variant="info">
            {verifiedAgo(connection.last_verified_at)}
          </Badge>
        )}
        <div className={styles.itemActions}>
          {canVerify && (
            <RobotoButton
              variant="ghost"
              size="sm"
              loading={verifyMutation.isPending}
              onClick={() =>
                verifyMutation.mutate({
                  orgId,
                  connectionId: connection.connection_id,
                })
              }
              aria-label={`Verify ${connection.label}`}
            >
              Verify
            </RobotoButton>
          )}
          {onEdit !== undefined && (
            <RobotoButton
              variant="ghost"
              size="sm"
              onClick={onEdit}
              aria-label={`Edit ${connection.label}`}
            >
              Edit
            </RobotoButton>
          )}
          {onDelete !== undefined && (
            <RobotoButton
              variant="ghost"
              size="sm"
              onClick={onDelete}
              aria-label={`Delete ${connection.label}`}
            >
              Delete
            </RobotoButton>
          )}
        </div>
      </div>
      <dl className={styles.details}>
        <dt>Kind</dt>
        <dd>{ORG_LLM_CONNECTION_KIND_LABELS[connection.settings.kind]}</dd>
        <dt>Endpoint</dt>
        <dd>{endpointLabel(connection)}</dd>
        <dt>API key secret</dt>
        <dd>{connection.settings.api_key_secret_name ?? "None"}</dd>
      </dl>
      {connection.last_error !== null && (
        <p className={styles.error}>Last error: {connection.last_error}</p>
      )}
      <VerifyOutcome
        result={verifyMutation.data}
        error={verifyMutation.error}
      />
    </li>
  );
}
